/**
 * Pure helpers for periodic heartbeat scheduling vs wall-clock anchor (`lastPeriodicHeartbeatSentAtMs`).
 * Used by {@link HeartbeatLoop} for visibility resume and unload final gating.
 */

export type ResumeCadenceDecision = { kind: 'send_then_schedule_full' } | { kind: 'schedule_remaining'; remainingMs: number };

export function resumePeriodicCadenceDecision(
  nowMs: number,
  lastPeriodicHeartbeatSentAtMs: number | null,
  intervalMs: number
): ResumeCadenceDecision {
  if (lastPeriodicHeartbeatSentAtMs == null) {
    return { kind: 'send_then_schedule_full' };
  }
  const elapsed = nowMs - lastPeriodicHeartbeatSentAtMs;
  if (elapsed >= intervalMs) {
    return { kind: 'send_then_schedule_full' };
  }

  return { kind: 'schedule_remaining', remainingMs: intervalMs - elapsed };
}

/** Same rule as resume cadence: skip redundant unload final if a periodic was enqueued within the interval window. */
export function shouldSkipRedundantUnloadFinal(
  nowMs: number,
  lastPeriodicHeartbeatSentAtMs: number | null,
  intervalMs: number
): boolean {
  if (lastPeriodicHeartbeatSentAtMs == null) {
    return false;
  }

  return nowMs - lastPeriodicHeartbeatSentAtMs < intervalMs;
}

/** Full timer delay: base interval slice plus symmetric jitter in [-jitterMs, +jitterMs], floored at 0. */
export function heartbeatScheduleDelayMs(
  baseIntervalMs: number,
  jitterMs: number
): number {
  const jitter = Math.floor((Math.random() * 2 - 1) * jitterMs);

  return Math.max(0, baseIntervalMs + jitter);
}
